/**
 * CloudMap Popup
 * Info window and popup content generation
 */

import {getProviderColor} from './constants.js';
import {calculateDistance, formatDistance} from './utils.js';

/**
 * Get nearby regions for a given region
 */
export function getNearbyRegions(currentRegion, data, activeProviders, limit = 3) {
    if (!data?.regions?.features) return [];

    const currentLat = currentRegion.geometry?.coordinates[1] || parseFloat(currentRegion.latitude);
    const currentLng = currentRegion.geometry?.coordinates[0] || parseFloat(currentRegion.longitude);
    const currentId = currentRegion.properties?.id || currentRegion.id;

    const nearby = [];

    data.regions.features.forEach(feature => {
        const props = feature.properties;
        if (props.id === currentId) return;
        if (!activeProviders.has(props.provider.slug)) return;

        const lat = feature.geometry.coordinates[1];
        const lng = feature.geometry.coordinates[0];
        const distance = calculateDistance(currentLat, currentLng, lat, lng);

        nearby.push({
            id: props.id,
            name: props.name,
            code: props.code,
            provider: props.provider,
            distance,
            lat,
            lng,
        });
    });

    nearby.sort((a, b) => a.distance - b.distance);
    return nearby.slice(0, limit);
}

/**
 * Create popup content HTML
 */
export function createPopupContent(data, mapData, activeProviders) {
    const {provider} = data;
    const statusClass = data.status?.toLowerCase().replace(/\s+/g, '-') || 'active';
    const statusText = data.status || 'Active';

    const notesHtml = data.notes
        ? `<div class="cloudmap-popup__notes">${data.notes}</div>`
        : '';

    const linkHtml = data.linkUrl
        ? `<a href="${data.linkUrl}" target="_blank" rel="noopener noreferrer" class="cloudmap-popup__link">
            ${data.linkText || 'Learn more'}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/>
            </svg>
           </a>`
        : '';

    const nearbyRegions = getNearbyRegions({
        properties: data,
        geometry: {coordinates: [parseFloat(data.longitude || 0), parseFloat(data.latitude || 0)]}
    }, mapData, activeProviders);

    let nearbyHtml = '';
    if (nearbyRegions.length > 0) {
        const nearbyItems = nearbyRegions.map(region => {
            const color = getProviderColor(region.provider.slug);
            return `
                <div class="cloudmap-popup__nearby-item" data-lat="${region.lat}" data-lng="${region.lng}">
                    <span class="cloudmap-popup__nearby-dot" style="background-color: ${color}"></span>
                    <span class="cloudmap-popup__nearby-name">${region.name}</span>
                    <span class="cloudmap-popup__nearby-distance">${formatDistance(region.distance)}</span>
                </div>
            `;
        }).join('');

        nearbyHtml = `
            <div class="cloudmap-popup__nearby">
                <div class="cloudmap-popup__nearby-label">Nearby Regions</div>
                ${nearbyItems}
            </div>
        `;
    }

    return `
        <div class="cloudmap-popup" style="--popup-provider-color: ${provider.color}">
            <div class="cloudmap-popup__header" style="background-color: ${provider.color}">
                <span class="cloudmap-popup__provider">${provider.name}</span>
                <span class="cloudmap-popup__status ${statusClass}">${statusText}</span>
            </div>
            <div class="cloudmap-popup__body">
                <h4 class="cloudmap-popup__title">${data.name}</h4>
                <code class="cloudmap-popup__code">${data.code}</code>

                <div class="cloudmap-popup__meta">
                    ${data.country ? `
                    <div class="cloudmap-popup__meta-row">
                        <span class="cloudmap-popup__meta-label">Location</span>
                        <span class="cloudmap-popup__meta-value">${data.city ? data.city + ', ' : ''}${data.country}</span>
                    </div>
                    ` : ''}
                    ${data.phase ? `
                    <div class="cloudmap-popup__meta-row">
                        <span class="cloudmap-popup__meta-label">Phase</span>
                        <span class="cloudmap-popup__meta-value">${data.phase}</span>
                    </div>
                    ` : ''}
                </div>

                ${data.availabilityZones ? `
                <div class="cloudmap-popup__az">
                    <div class="cloudmap-popup__az-label">Availability Zones</div>
                    <div class="cloudmap-popup__az-value">${data.availabilityZones}</div>
                </div>
                ` : ''}

                ${notesHtml}
                ${linkHtml}
                ${nearbyHtml}
            </div>
        </div>
    `;
}

/**
 * Setup nearby region click handlers
 */
export function setupNearbyClickHandlers(infoWindow, map) {
    google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
        document.querySelectorAll('.cloudmap-popup__nearby-item').forEach(item => {
            item.addEventListener('click', () => {
                const lat = parseFloat(item.dataset.lat);
                const lng = parseFloat(item.dataset.lng);
                if (!isNaN(lat) && !isNaN(lng)) {
                    infoWindow.close();
                    map.panTo({lat, lng});
                    map.setZoom(Math.max(map.getZoom(), 6));
                }
            });
        });
    });
}
